import { useDeferredValue, useId, useState } from 'react';
import { SOURCE_PAGE_SIZE, useSource, useSourceSearch } from '../api/queries';
import type { SourceKind } from '../api/types';
import { availabilityMeta, sourceKindLabel } from '../lib/format';
import { ErrorState, Loading } from './States';

/**
 * Chooses one source through server-side search with bounded pages. Results
 * say how many of the matching total are shown; loading more is explicit.
 */
export function SourcePicker({
  label,
  value,
  onChange,
  emptyLabel = 'None chosen',
  help,
}: {
  label: string;
  value: string;
  onChange: (sourceId: string) => void;
  emptyLabel?: string;
  help?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<SourceKind | ''>('');
  const query = useDeferredValue(q.trim());
  const selected = useSource(value, value !== '');
  const search = useSourceSearch({ ...(query !== '' ? { q: query } : {}), ...(kind !== '' ? { kind } : {}) }, open);
  const items = search.data?.pages.flatMap((page) => page.items) ?? [];
  const total = search.data?.pages[0]?.total;

  const toggleText = open ? 'Close search' : value === '' ? 'Search sources' : 'Change';
  let current = emptyLabel;
  if (value !== '') {
    if (selected.data !== undefined) current = selected.data.source.title;
    else if (selected.isError) current = 'Source not visible to your account';
    else current = 'Loading source…';
  }

  return (
    <div className="source-picker" role="group" aria-labelledby={`${id}-label`}>
      <span className="field__label" id={`${id}-label`}>
        {label}
      </span>
      {help !== undefined && <p className="field__help">{help}</p>}
      <div className="source-picker__current">
        <span className={value === '' ? 'muted' : 'source-picker__title'}>{current}</span>
        <button
          type="button"
          className="button button--link"
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          aria-label={`${toggleText} for ${label}`}
          onClick={() => setOpen((state) => !state)}
        >
          {toggleText}
        </button>
        {value !== '' && (
          <button type="button" className="button button--link" aria-label={`Clear ${label}`} onClick={() => onChange('')}>
            Clear
          </button>
        )}
      </div>
      {open && (
        <div className="source-picker__panel" id={`${id}-panel`}>
          <div className="source-picker__query">
            <label className="field">
              <span className="field__label">Search titles and shared text</span>
              <input type="search" className="input" value={q} onChange={(event) => setQ(event.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Kind</span>
              <select className="input" value={kind} onChange={(event) => setKind(event.target.value as SourceKind | '')}>
                <option value="">Any kind</option>
                {Object.entries(sourceKindLabel).map(([key, text]) => (
                  <option key={key} value={key}>
                    {text}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {search.isPending && <Loading label="Searching sources" />}
          {search.isError && <ErrorState error={search.error} onRetry={() => void search.refetch()} />}
          {search.data !== undefined && (
            <>
              <p className="source-picker__count" role="status" aria-live="polite">
                {total === undefined
                  ? `Showing ${String(items.length)}`
                  : `Showing ${String(items.length)} of ${total.toLocaleString('en-GB')} matching ${total === 1 ? 'source' : 'sources'}`}
              </p>
              {items.length === 0 ? (
                <p className="muted">No sources you can see match.</p>
              ) : (
                <ul className="source-picker__results">
                  {items.map((source) => (
                    <li key={source.id}>
                      <button
                        type="button"
                        className={source.id === value ? 'source-option source-option--selected' : 'source-option'}
                        aria-pressed={source.id === value}
                        onClick={() => {
                          onChange(source.id);
                          setOpen(false);
                        }}
                      >
                        <span className="source-option__title">{source.title}</span>
                        <span className="source-option__meta">
                          {sourceKindLabel[source.kind]} · {availabilityMeta[source.availability].label}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {search.hasNextPage && (
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={search.isFetchingNextPage}
                  onClick={() => void search.fetchNextPage()}
                >
                  {search.isFetchingNextPage ? 'Loading…' : `Load ${String(SOURCE_PAGE_SIZE)} more`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
